import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Box, Grid, Stack, Typography } from '@mui/material';
import moment from 'moment/moment';

import TextFieldFormik from '../../../../common/text_field';
import { SubmitButton } from '../../../../common/button';
import { getEmployerState } from '../../../../../features/benefit/get_employer';
import useNotification from '../../../../../core/hooks/use_notification';
import { updateEmployerAction, updateEmployerState } from '../../../../../features/benefit/update_employer';
import { updateEmployerNotificationData, updateEmployerStatusNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { getButtonText, getEmployerBillingScheduleType, getEmployerStatus, getUpdateEmployerStatus } from '../../../../common/utils/employer_utils';
import { updateEmployerStatusAction, updateEmployerStatusState } from '../../../../../features/benefit/update_employer_status';
import { yesNoOptions } from '../../../../common/form_utils';
import RadioGroupFormik from '../../../../common/radio_group';
import { SelectFieldFormik } from '../../../../common/select_field';
import { getAllHubsState } from '../../../../../features/pop/get_all_hubs';
import { getEmployerBillingScheduleState } from '../../../../../features/benefit/get_employer_billing_schedule';
import UpdateEmployerBillingScheduleDialog from './update_employer_billing_schedule_dialog';
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';

const EmployerForm = ({ refreshEmployer }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getEmployer = useSelector(getEmployerState);
    const getEmployerBillingSchedule = useSelector(getEmployerBillingScheduleState);
    const getAllHubs = useSelector(getAllHubsState);
    const updateEmployerStatus = useSelector(updateEmployerStatusState);
    const updateEmployer = useSelector(updateEmployerState);

    const initialValues = {
        name: getEmployer?.data?.name || '',
        nip: getEmployer?.data?.nip || '',
        address: getEmployer?.data?.address || '',
        mailingAddress: getEmployer?.data?.mailingAddress || '',
        supportDeductFromSalary: getEmployer?.data?.supportDeductFromSalary || false,
        supportedHubId: getEmployer?.data?.supportedHubId || null
    };

    const schema = Yup.object().shape({
        name: Yup.string().required(),
        nip: Yup.string().required(),
        address: Yup.string().required(),
        mailingAddress: Yup.string()
            .email('Wprowadź prawidłowy email')
            .nullable()
            .notRequired(),
        supportDeductFromSalary: Yup.bool().required(),
        supportedHubId: Yup.number().nullable()
    });

    const onUpdateEmployerStatus = (employer) => {
        const employerStatus = getUpdateEmployerStatus(employer?.status);

        dispatch(updateEmployerStatusAction({ employerId: employer?.id, form: { command: employerStatus } }))
            .then(response => {
                showNotification(updateEmployerStatusNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshEmployer();
            });
    };

    const onSubmit = (values) => {
        dispatch(updateEmployerAction({ employerId: getEmployer?.data?.id, form: values }))
            .then(response => {
                showNotification(updateEmployerNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshEmployer();
            });
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
        >
            { () => (
                <Box component={ Form }
                     sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <Stack spacing={ 3 }>

                            <SectionPaper title="Konto pracodawcy">
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Bieżący status operacyjny konta kontrolowanego podmiotu:
                                        </Typography>
                                        <Typography variant="h6" sx={ { mt: 0.5 } }>
                                            Status: <Box component="span"
                                                         fontWeight="bold">{ getEmployerStatus(getEmployer?.data?.status) }</Box>
                                        </Typography>
                                    </Box>
                                    <SubmitButton
                                        type="button"
                                        onClick={ () => onUpdateEmployerStatus(getEmployer?.data) }
                                        isLoading={ updateEmployerStatus?.loading }
                                        sx={ { width: 160 } }
                                    >
                                        { getButtonText(getEmployer?.data?.status) }
                                    </SubmitButton>
                                </Stack>
                            </SectionPaper>

                            <SectionPaper title="Finanse i rozliczenia">
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Harmonogram rozliczania: <Box component="span"
                                                                          fontWeight="bold">{ getEmployerBillingScheduleType(getEmployerBillingSchedule?.data?.type) }</Box>
                                        </Typography>
                                        <Stack spacing={ 0.5 } sx={ { mt: 1 } }>
                                            <Typography variant="body2" color="text.secondary">
                                                Ostatnia data rozliczenia:{ ' ' }
                                                <Box component="span" color="text.primary" fontWeight="500">
                                                    { getEmployerBillingSchedule?.data?.lastInvoiceDate ? moment(getEmployerBillingSchedule?.data?.lastInvoiceDate).format('DD.MM.YYYY') : '-' }
                                                </Box>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Następna data rozliczenia:{ ' ' }
                                                <Box component="span" color="text.primary" fontWeight="500">
                                                    { getEmployerBillingSchedule?.data?.nextInvoiceDate ? moment(getEmployerBillingSchedule?.data?.nextInvoiceDate).format('DD.MM.YYYY') : '-' }
                                                </Box>
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    <UpdateEmployerBillingScheduleDialog
                                        type={ getEmployerBillingSchedule?.data?.type }
                                        refreshEmployer={ refreshEmployer }
                                        buttonSx={ { width: 160 } }
                                    />
                                </Stack>
                            </SectionPaper>

                            <SectionPaper title="Dane szczegółowe firmy">
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="name" label="Nazwa" required fullWidth/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="nip" label="NIP" required fullWidth/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="address" label="Adres" required fullWidth/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="mailingAddress" label="Email" fullWidth/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <SelectFieldFormik
                                            name="supportedHubId"
                                            label="Umożliwiaj zamówienia w ramach hub"
                                            options={ [{
                                                value: null,
                                                label: 'Nie wybrano'
                                            }, ...getAllHubs?.data?.map(hub => ({
                                                value: hub?.id,
                                                label: hub?.name
                                            })) || []] }
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <RadioGroupFormik
                                            name="supportDeductFromSalary"
                                            label="Czy wspiera pobranie z wynagrodzenia"
                                            options={ yesNoOptions }
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
                            <SubmitButton isLoading={ updateEmployer?.loading } sx={ { width: 160 } }>
                                Zapisz zmiany
                            </SubmitButton>
                        </Box>
                    </BottomActionBar>

                </Box>
            ) }
        </Formik>
    );
};

export default EmployerForm;